import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import AthenaPage from "./pages/AthenaPage";
import WorkspacePage from "./pages/WorkspacePage";
import VisualCallPage from "./pages/VisualCallPage";
import { OnboardingModal } from "./components/OnboardingModal";
import { QuickAthenaSetup } from "./components/QuickAthenaSetup";
import { NativeAppShell } from "./platform/NativeAppShell";
import { PendingDeviceContextProvider } from "./capabilities/PendingDeviceContext";
import { AthenaDeviceContextProvider } from "./context/AthenaDeviceContextProvider";
import { AthenaRemoteConfigProvider } from "./config/RemoteConfigProvider";
import { AthenaIntentProvider } from "./platform/AthenaIntentRouter";
import { AthenaEventEngineProvider } from "./events/AthenaEventEngineProvider";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030308] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Instalações Android antigas guardaram /workspace como start_url. Só na
// abertura inicial em modo app, convertemos essa rota legada para a Home; as
// navegações normais para Workspace dentro da Athena continuam funcionando.
function LegacyInstalledAppHomeRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    const isInstalled = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isInstalled && location.pathname === '/workspace') navigate('/', { replace: true });
  }, [location.pathname, navigate]);

  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const [onboardingDone, setOnboardingDone] = useState(
    () => !!localStorage.getItem('athena_onboarding_done')
  );
  // NativeAppShell only needs the token for an already-enabled native push
  // refresh. It never uses this value to activate FCM on app boot.
  const authToken = user ? localStorage.getItem('athena_token') : null;

  return (
    <>
      <NativeAppShell
        appReady={!loading}
        authenticated={Boolean(user)}
        authToken={authToken}
      />
      <LegacyInstalledAppHomeRedirect />
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/" element={<ProtectedRoute><AthenaPage /></ProtectedRoute>} />
        <Route path="/workspace/*" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
        <Route path="/visual-call" element={<ProtectedRoute><VisualCallPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {user && !onboardingDone && (
        <OnboardingModal
          onComplete={() => setOnboardingDone(true)}
          onSkip={() => setOnboardingDone(true)}
        />
      )}
      <QuickAthenaSetup active={Boolean(user) && onboardingDone} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AthenaEventEngineProvider>
            <AthenaRemoteConfigProvider>
              <AthenaDeviceContextProvider>
                <AthenaIntentProvider>
                  <PendingDeviceContextProvider>
                    <AppRoutes />
                  </PendingDeviceContextProvider>
                </AthenaIntentProvider>
              </AthenaDeviceContextProvider>
            </AthenaRemoteConfigProvider>
          </AthenaEventEngineProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
